import type { TrendPoint as ApiTrendPoint } from '@/api/pulse';

// Collapsing the org-wide pulse timeline to one point per ROUND.
//
// A pulse round is sent as three separate campaigns — one per target role
// (member / guardian / leader) — so `GET /pulse/trend?role=all`, which drops the
// role filter deliberately, returns three points sharing the same date. Plotted
// raw that produces a cluster of up to three dots per round, breaks in the line
// wherever one role's run was below the anonymity floor (null), duplicated x
// labels, and — worst — a "change since previous" that compares two ROLES from
// the same round instead of two rounds.
//
// One club-wide number per round is what the club actually sent and what a
// trend should compare, so the roles are pooled here.

export interface PulseRound {
  // Date of the round (the runs' shared close/created date).
  at: string;
  // Response-weighted mean of the roles that produced a score, or null when
  // none did.
  enps: number | null;
  // Total responses across the round's runs.
  n: number;
  // How many of the round's runs reported a score — surfaced so a caller can
  // say "2 of 3 audiences" if it ever matters.
  scoredRuns: number;
}

// dayKey buckets by UTC calendar day: the three role-runs of one round share a
// timestamp, and using the day (not the exact instant) keeps them together even
// if a scheduler staggers them by minutes.
function dayKey(iso: string): string {
  return iso.slice(0, 10);
}

export function collapsePulseRounds(points: ApiTrendPoint[]): PulseRound[] {
  const byDay = new Map<string, { at: string; weighted: number; weight: number; n: number; scored: number }>();
  for (const p of points) {
    const key = dayKey(p.at);
    const acc = byDay.get(key) ?? { at: p.at, weighted: 0, weight: 0, n: 0, scored: 0 };
    acc.n += p.n;
    if (p.enps != null) {
      // Weighted by responses, not a plain mean of means: eNPS is
      // (%promoters − %detractors), i.e. a mean over responses, so weighting by
      // each run's response count reconstructs the pooled club-wide score
      // instead of letting a 3-response leader run count as much as a
      // 300-response member run.
      const w = p.n > 0 ? p.n : 1;
      acc.weighted += p.enps * w;
      acc.weight += w;
      acc.scored += 1;
    }
    byDay.set(key, acc);
  }
  return [...byDay.entries()]
    .sort((a, b) => (a[0] < b[0] ? -1 : a[0] > b[0] ? 1 : 0))
    .map(([, v]) => ({
      at: v.at,
      enps: v.weight > 0 ? v.weighted / v.weight : null,
      n: v.n,
      scoredRuns: v.scored,
    }));
}
